import type { GameEvent, UserId } from '../../types';
import { RuleError } from '../../errors';
import {
  DEFAULT_BATTLESHIP_CONFIG,
  FLEET,
  FLEET_BY_KIND,
  GRID_SIZE,
  type BattleshipConfig,
  type BattleshipState,
  type BattleshipView,
  type Placement,
  type PlacedShip,
  type PlayerBoard,
  type RevealedShip,
  type Shot,
  type ShotResult,
} from './types';

export interface Cell {
  x: number;
  y: number;
}

/** Cases occupées par un navire donné. */
export function cellsOf(placement: Placement, size: number): Cell[] {
  const cells: Cell[] = [];
  for (let i = 0; i < size; i += 1) {
    cells.push({
      x: placement.orientation === 'h' ? placement.x + i : placement.x,
      y: placement.orientation === 'v' ? placement.y + i : placement.y,
    });
  }
  return cells;
}

export function shipCells(ship: PlacedShip): Cell[] {
  return cellsOf(ship, ship.size);
}

export function isSunk(ship: PlacedShip): boolean {
  return ship.hits.every(Boolean);
}

export function shipsRemaining(board: PlayerBoard): number {
  return board.ships.filter((s) => !isSunk(s)).length;
}

/**
 * Vérifie qu'une flotte proposée est complète, dans la grille et sans chevauchement.
 * Lève une `RuleError` explicite sinon. Les navires peuvent se toucher.
 */
export function validateFleet(placements: Placement[]): void {
  if (placements.length !== FLEET.length) {
    throw new RuleError(`La flotte doit compter exactement ${FLEET.length} navires.`);
  }

  const seen = new Set<string>();
  const occupied = new Map<string, string>();

  for (const placement of placements) {
    const spec = FLEET_BY_KIND[placement.kind];
    if (!spec) {
      throw new RuleError(`Type de navire inconnu : ${placement.kind}.`);
    }
    if (seen.has(placement.kind)) {
      throw new RuleError(`Le navire « ${spec.label} » est placé plusieurs fois.`);
    }
    seen.add(placement.kind);

    for (const cell of cellsOf(placement, spec.size)) {
      if (cell.x < 0 || cell.y < 0 || cell.x >= GRID_SIZE || cell.y >= GRID_SIZE) {
        throw new RuleError(`Le navire « ${spec.label} » dépasse de la grille.`);
      }
      const key = `${cell.x},${cell.y}`;
      const other = occupied.get(key);
      if (other) {
        throw new RuleError(`« ${spec.label} » chevauche « ${other} ».`);
      }
      occupied.set(key, spec.label);
    }
  }

  if (seen.size !== FLEET.length) {
    throw new RuleError('La flotte est incomplète.');
  }
}

function emptyBoard(): PlayerBoard {
  return { ships: [], ready: false, incoming: [] };
}

function boardOf(state: BattleshipState, player: UserId): PlayerBoard {
  const board = state.boards[player];
  if (!board) throw new RuleError("Ce joueur ne participe pas à cette partie.");
  return board;
}

export function opponentOf(state: BattleshipState, player: UserId): UserId {
  const other = state.seats.find((seat) => seat !== player);
  if (!other) throw new RuleError("Impossible de déterminer l'adversaire.");
  return other;
}

function placementDeadline(config: BattleshipConfig, now: number): number | null {
  return config.placementSeconds === null ? null : now + config.placementSeconds * 1000;
}

function turnDeadline(config: BattleshipConfig, now: number): number | null {
  return config.turnSeconds === null ? null : now + config.turnSeconds * 1000;
}

export function createInitialState(
  seats: UserId[],
  config: BattleshipConfig = DEFAULT_BATTLESHIP_CONFIG,
  now = 0,
): BattleshipState {
  if (seats.length !== 2 || seats[0] === seats[1]) {
    throw new RuleError('La bataille navale se joue à exactement deux joueurs distincts.');
  }
  const boards: Record<UserId, PlayerBoard> = {};
  for (const seat of seats) boards[seat] = emptyBoard();

  return {
    version: 1,
    phase: 'placement',
    seats: [...seats],
    boards,
    turn: null,
    winner: null,
    endReason: null,
    config,
    deadlineAt: placementDeadline(config, now),
    moves: 0,
  };
}

export interface RuleResult {
  state: BattleshipState;
  events: GameEvent[];
}

/** Enregistre la flotte d'un joueur ; démarre la bataille quand les deux sont prêts. */
export function applyPlace(
  state: BattleshipState,
  player: UserId,
  placements: Placement[],
  now: number,
): RuleResult {
  if (state.phase !== 'placement') {
    throw new RuleError('La phase de placement est terminée.');
  }
  const board = boardOf(state, player);
  if (board.ready) {
    throw new RuleError('Votre flotte est déjà validée.');
  }
  validateFleet(placements);

  const next = structuredClone(state);
  const nextBoard = boardOf(next, player);
  nextBoard.ships = placements.map((p) => {
    const spec = FLEET_BY_KIND[p.kind];
    return {
      kind: p.kind,
      x: p.x,
      y: p.y,
      orientation: p.orientation,
      size: spec.size,
      hits: new Array<boolean>(spec.size).fill(false),
    } satisfies PlacedShip;
  });
  nextBoard.ready = true;

  const events: GameEvent[] = [{ type: 'fleet_ready', actorId: player }];

  const everyoneReady = next.seats.every((seat) => boardOf(next, seat).ready);
  if (everyoneReady) {
    next.phase = 'playing';
    next.turn = next.seats[0] ?? null;
    next.deadlineAt = turnDeadline(next.config, now);
    events.push({ type: 'battle_started', actorId: next.turn ?? undefined });
  }

  return { state: next, events };
}

/** Tir sur la grille adverse. */
export function applyFire(
  state: BattleshipState,
  player: UserId,
  x: number,
  y: number,
  now: number,
): RuleResult {
  if (state.phase !== 'playing') {
    throw new RuleError("La partie n'est pas en cours.");
  }
  if (state.turn !== player) {
    throw new RuleError("Ce n'est pas votre tour.");
  }
  if (x < 0 || y < 0 || x >= GRID_SIZE || y >= GRID_SIZE) {
    throw new RuleError('Cette case est hors de la grille.');
  }

  const targetId = opponentOf(state, player);
  const target = boardOf(state, targetId);
  if (target.incoming.some((shot) => shot.x === x && shot.y === y)) {
    throw new RuleError('Vous avez déjà tiré sur cette case.');
  }

  const next = structuredClone(state);
  const nextTarget = boardOf(next, targetId);

  let result: ShotResult = 'miss';
  let hitShip: PlacedShip | undefined;
  for (const ship of nextTarget.ships) {
    const index = shipCells(ship).findIndex((cell) => cell.x === x && cell.y === y);
    if (index >= 0) {
      ship.hits[index] = true;
      hitShip = ship;
      result = isSunk(ship) ? 'sunk' : 'hit';
      break;
    }
  }

  const shot: Shot = { x, y, result };
  if (result === 'sunk' && hitShip) shot.kind = hitShip.kind;
  nextTarget.incoming.push(shot);
  next.moves += 1;

  const events: GameEvent[] = [
    { type: 'shot', actorId: player, x, y, result, kind: shot.kind },
  ];
  if (result === 'sunk' && hitShip) {
    events.push({ type: 'ship_sunk', actorId: player, kind: hitShip.kind });
  }

  if (shipsRemaining(nextTarget) === 0) {
    next.phase = 'finished';
    next.winner = player;
    next.endReason = 'victory';
    next.turn = null;
    next.deadlineAt = null;
    events.push({ type: 'finished', actorId: player, reason: 'victory' });
    return { state: next, events };
  }

  const keepTurn = next.config.extraTurnOnHit && result !== 'miss';
  next.turn = keepTurn ? player : targetId;
  next.deadlineAt = turnDeadline(next.config, now);

  return { state: next, events };
}

/** Abandon volontaire. */
export function applyResign(state: BattleshipState, player: UserId): RuleResult {
  if (state.phase === 'finished') {
    throw new RuleError('La partie est déjà terminée.');
  }
  boardOf(state, player);

  const next = structuredClone(state);
  next.phase = 'finished';
  next.winner = opponentOf(next, player);
  next.endReason = 'resign';
  next.turn = null;
  next.deadlineAt = null;

  return {
    state: next,
    events: [{ type: 'finished', actorId: next.winner, reason: 'resign', resignedBy: player }],
  };
}

/**
 * Dépassement du chronomètre. Pendant le placement, le ou les joueurs en retard
 * perdent ; en cours de bataille, c'est le joueur dont c'est le tour.
 */
export function applyTimeout(state: BattleshipState): RuleResult {
  if (state.phase === 'finished') {
    throw new RuleError('La partie est déjà terminée.');
  }

  const next = structuredClone(state);
  next.phase = 'finished';
  next.endReason = 'timeout';
  next.turn = null;
  next.deadlineAt = null;

  if (state.phase === 'placement') {
    const late = next.seats.filter((seat) => !boardOf(next, seat).ready);
    // Les deux joueurs en retard : personne ne gagne.
    next.winner = late.length === 1 && late[0] ? opponentOf(next, late[0]) : null;
  } else {
    const loser = state.turn;
    next.winner = loser ? opponentOf(next, loser) : null;
  }

  return {
    state: next,
    events: [{ type: 'finished', actorId: next.winner ?? undefined, reason: 'timeout' }],
  };
}

/** Interruption administrative : la partie est annulée, sans vainqueur. */
export function applyAdminAbort(state: BattleshipState): RuleResult {
  const next = structuredClone(state);
  next.phase = 'finished';
  next.winner = null;
  next.endReason = 'admin';
  next.turn = null;
  next.deadlineAt = null;
  return { state: next, events: [{ type: 'finished', reason: 'admin' }] };
}

function revealedSunk(board: PlayerBoard): RevealedShip[] {
  return board.ships
    .filter(isSunk)
    .map((ship) => ({
      kind: ship.kind,
      x: ship.x,
      y: ship.y,
      orientation: ship.orientation,
      size: ship.size,
    }));
}

/**
 * Projection de l'état pour un joueur : sa grille complète, et de la grille adverse
 * uniquement ce qu'il a découvert. Les positions adverses non coulées ne sortent
 * jamais d'ici.
 */
export function viewFor(state: BattleshipState, viewer: UserId): BattleshipView {
  const you = boardOf(state, viewer);
  const opponentId = opponentOf(state, viewer);
  const opponent = boardOf(state, opponentId);
  const revealAll = state.phase === 'finished';

  return {
    phase: state.phase,
    youId: viewer,
    opponentId,
    turn: state.turn,
    winner: state.winner,
    endReason: state.endReason,
    config: state.config,
    deadlineAt: state.deadlineAt,
    moves: state.moves,
    you: {
      ships: structuredClone(you.ships),
      ready: you.ready,
      incoming: structuredClone(you.incoming),
      shipsRemaining: shipsRemaining(you),
    },
    opponent: {
      ready: opponent.ready,
      shots: structuredClone(opponent.incoming),
      // En fin de partie, la flotte adverse est intégralement dévoilée.
      sunk: revealAll
        ? opponent.ships.map((ship) => ({
            kind: ship.kind,
            x: ship.x,
            y: ship.y,
            orientation: ship.orientation,
            size: ship.size,
          }))
        : revealedSunk(opponent),
      shipsRemaining: shipsRemaining(opponent),
    },
  };
}

/** Tire une flotte complète au hasard (utilisé par le bouton « placement aléatoire »). */
export function randomPlacements(rand: () => number = Math.random): Placement[] {
  const occupied = new Set<string>();
  const placements: Placement[] = [];

  for (const spec of FLEET) {
    for (let attempt = 0; ; attempt += 1) {
      if (attempt > 500) throw new RuleError('Placement aléatoire impossible.');
      const orientation: Placement['orientation'] = rand() < 0.5 ? 'h' : 'v';
      const span = GRID_SIZE - spec.size + 1;
      const x = Math.floor(rand() * (orientation === 'h' ? span : GRID_SIZE));
      const y = Math.floor(rand() * (orientation === 'v' ? span : GRID_SIZE));
      const candidate: Placement = { kind: spec.kind, x, y, orientation };
      const cells = cellsOf(candidate, spec.size);
      if (cells.some((c) => occupied.has(`${c.x},${c.y}`))) continue;
      cells.forEach((c) => occupied.add(`${c.x},${c.y}`));
      placements.push(candidate);
      break;
    }
  }

  return placements;
}
